/**
 * Phase 6C — subscription banner.
 *
 * Mounts inside the Cockpit shell. Polls /billing/subscription/status on
 * mount + on `subscription:blocked` events (dispatched by the API client
 * when a 402 lands). Renders:
 *   - past_due  → amber banner with grace countdown
 *   - canceled  → red banner + read-only hint
 *   - suspended → red full-width takeover (hard block)
 *   - other     → no banner
 *
 * The actual gate is server-side; this is purely informational + nudges
 * the user to the billing page.
 */

import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import { AlertTriangle, ArrowRight, CreditCard, X } from 'lucide-react'

import { billing, type SubscriptionStatus } from '../api/client'
import { Button } from './ui/button'

export default function SubscriptionBanner() {
  const { t } = useTranslation()
  const nav = useNavigate()
  const [state, setState] = useState<SubscriptionStatus | null>(null)
  const [dismissed, setDismissed] = useState(false)

  // Initial fetch + listen for 402 events (which mean we just got blocked
  // mid-flight, so the cached state is stale).
  useEffect(() => {
    let alive = true
    const load = async () => {
      try {
        const s = await billing.getSubscriptionStatus()
        if (alive) {
          setState(s)
          // Reset dismissal when state changes (e.g. just paid or just
          // entered grace — the banner SHOULD reappear).
          setDismissed(false)
        }
      } catch {
        /* unauth or network — banner stays hidden */
      }
    }
    load()
    // Debounce the refetch in case a page fires many writes into a 402
    // wall — we get one event per blocked request, but want only one
    // status refetch.
    let pending: ReturnType<typeof setTimeout> | null = null
    const onBlocked = () => {
      if (pending) clearTimeout(pending)
      pending = setTimeout(() => {
        pending = null
        load()
      }, 250)
    }
    window.addEventListener('subscription:blocked', onBlocked)
    return () => {
      alive = false
      if (pending) clearTimeout(pending)
      window.removeEventListener('subscription:blocked', onBlocked)
    }
  }, [])

  if (!state || dismissed) return null
  if (state.is_demo) return null
  if (
    state.effective_status === null ||
    state.effective_status === 'active' ||
    state.effective_status === 'trialing' ||
    state.effective_status === 'comped'
  ) {
    return null
  }

  const isCritical =
    state.effective_status === 'canceled' || state.effective_status === 'suspended'

  return (
    <div
      role="status"
      className={
        isCritical
          ? 'flex items-center gap-3 border-b border-destructive/40 bg-destructive/10 px-4 py-2.5 text-sm text-destructive'
          : 'flex items-center gap-3 border-b border-amber-500/40 bg-amber-50 px-4 py-2.5 text-sm text-amber-900'
      }
    >
      <AlertTriangle className="size-4 shrink-0" />
      <div className="flex-1 min-w-0">
        <span className="font-medium">
          {t(`billing.banner.${state.effective_status}`)}
        </span>
        {state.grace_days_remaining > 0 && (
          <span className="ml-1.5 opacity-80">
            {t('billing.banner.graceLeft', { days: state.grace_days_remaining })}
          </span>
        )}
      </div>
      <Button
        size="sm"
        variant={isCritical ? 'destructive' : 'outline'}
        onClick={() => nav('/settings/billing')}
      >
        <CreditCard className="size-3.5" />
        {t('billing.banner.payNow')}
        <ArrowRight className="size-3.5" />
      </Button>
      {!isCritical && (
        <button
          type="button"
          aria-label={t('billing.banner.dismiss')}
          onClick={() => setDismissed(true)}
          className="rounded-md p-1 hover:bg-amber-100"
        >
          <X className="size-3.5" />
        </button>
      )}
    </div>
  )
}
